"use client";

import * as React from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  X,
  Mic,
  MicOff,
  ArrowRight,
  Clock,
  Sprout,
  Mountain,
  Leaf,
  TrendingUp,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useMobileNavigation } from "./MobileNavigationContext";
import { Input } from "@/components/ui/input";

interface SearchSuggestion {
  id: string;
  type: "recent" | "popular" | "category";
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
}

const defaultSuggestions: SearchSuggestion[] = [
  {
    id: "desert",
    type: "category",
    label: "Desert Terrariums",
    icon: Leaf,
    path: "/checkout?biome=desert",
  },
  {
    id: "forest",
    type: "category",
    label: "Forest Terrariums",
    icon: Sprout,
    path: "/checkout?biome=forest",
  },
  {
    id: "workshop",
    type: "popular",
    label: "Nature Workshops",
    icon: Mountain,
    path: "/workshop",
  },
  {
    id: "custom",
    type: "popular",
    label: "Custom Designs",
    icon: TrendingUp,
    path: "/checkout?tier=custom",
  },
];

export function MobileSearchOverlay() {
  const {
    isSearchOpen,
    setIsSearchOpen,
    searchQuery,
    setSearchQuery,
    isVoiceSearchActive,
    startVoiceSearch,
    stopVoiceSearch,
  } = useMobileNavigation();
  const [, setLocation] = useLocation();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [recentSearches, setRecentSearches] = React.useState<string[]>([]);
  const [filteredSuggestions, setFilteredSuggestions] = React.useState<SearchSuggestion[]>(defaultSuggestions);

  // Load recent searches from localStorage
  React.useEffect(() => {
    const saved = localStorage.getItem("lifeitself_recent_searches");
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch {
        // Ignore parsing errors
      }
    }
  }, []);

  // Filter suggestions based on search query
  React.useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredSuggestions(defaultSuggestions);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = defaultSuggestions.filter(
      (suggestion) =>
        suggestion.label.toLowerCase().includes(query) ||
        suggestion.type.includes(query)
    );
    setFilteredSuggestions(filtered);
  }, [searchQuery]);

  // Focus input when overlay opens
  React.useEffect(() => {
    if (isSearchOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isSearchOpen]);

  const handleClose = React.useCallback(() => {
    setIsSearchOpen(false);
    setSearchQuery("");
  }, [setIsSearchOpen, setSearchQuery]);

  const handleSearch = React.useCallback(
    (query: string) => {
      if (!query.trim()) return;

      // Save to recent searches
      const updated = [query, ...recentSearches.filter((s) => s !== query)].slice(0, 5);
      setRecentSearches(updated);
      localStorage.setItem("lifeitself_recent_searches", JSON.stringify(updated));

      // Navigate to search results or checkout with query
      setLocation(`/checkout?search=${encodeURIComponent(query)}`);
      handleClose();
    },
    [recentSearches, setLocation, handleClose]
  );

  const handleSuggestionClick = React.useCallback(
    (suggestion: SearchSuggestion) => {
      setLocation(suggestion.path);
      handleClose();
    },
    [setLocation, handleClose]
  );

  const handleVoiceToggle = React.useCallback(() => {
    if (isVoiceSearchActive) {
      stopVoiceSearch();
    } else {
      startVoiceSearch();
    }
  }, [isVoiceSearchActive, startVoiceSearch, stopVoiceSearch]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSearch(searchQuery);
      } else if (e.key === "Escape") {
        handleClose();
      }
    },
    [searchQuery, handleSearch, handleClose]
  );

  // Clear a recent search
  const clearRecentSearch = React.useCallback(
    (query: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const updated = recentSearches.filter((s) => s !== query);
      setRecentSearches(updated);
      localStorage.setItem("lifeitself_recent_searches", JSON.stringify(updated));
    },
    [recentSearches]
  );

  return (
    <AnimatePresence>
      {isSearchOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
            aria-hidden="true"
          />

          {/* Search Overlay */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{
              type: "spring",
              damping: 30,
              stiffness: 300,
            }}
            className={cn(
              "fixed inset-x-0 top-0 z-[70]",
              "bg-background",
              "flex flex-col",
              "max-h-[85vh]",
              "rounded-b-2xl shadow-xl"
            )}
            role="dialog"
            aria-modal="true"
            aria-label="Search"
            data-testid="mobile-search-overlay"
          >
            {/* Search Input Header */}
            <div className="flex items-center gap-3 p-4 border-b border-border">
              <Search className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              <Input
                ref={inputRef}
                type="text"
                placeholder="Search terrariums..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                className={cn(
                  "flex-1 border-0 bg-transparent p-0 h-auto",
                  "text-lg placeholder:text-muted-foreground/60",
                  "focus-visible:ring-0 focus-visible:ring-offset-0"
                )}
                data-testid="search-input"
              />

              {/* Voice Search Button */}
              <button
                onClick={handleVoiceToggle}
                className={cn(
                  "p-2 rounded-full transition-colors",
                  isVoiceSearchActive
                    ? "bg-red-500 text-white animate-pulse"
                    : "bg-muted hover:bg-muted/80"
                )}
                aria-label={isVoiceSearchActive ? "Stop voice search" : "Start voice search"}
                data-testid="voice-search-button"
              >
                {isVoiceSearchActive ? (
                  <MicOff className="w-5 h-5" />
                ) : (
                  <Mic className="w-5 h-5" />
                )}
              </button>

              {/* Close Button */}
              <button
                onClick={handleClose}
                className="p-2 rounded-full bg-muted hover:bg-muted/80 transition-colors"
                aria-label="Close search"
                data-testid="search-close-button"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Voice Search Indicator */}
            <AnimatePresence>
              {isVoiceSearchActive && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-red-50 dark:bg-red-950 px-4 py-3 flex items-center gap-3"
                >
                  <div className="flex gap-1">
                    {[...Array(3)].map((_, i) => (
                      <motion.div
                        key={i}
                        animate={{
                          scaleY: [1, 1.5, 1],
                        }}
                        transition={{
                          repeat: Infinity,
                          duration: 0.5,
                          delay: i * 0.1,
                        }}
                        className="w-1 h-4 bg-red-500 rounded-full"
                      />
                    ))}
                  </div>
                  <span className="text-sm text-red-700 dark:text-red-300">
                    Listening... speak now
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Search Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              {/* Recent Searches */}
              {recentSearches.length > 0 && !searchQuery && (
                <div className="px-4 py-4">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Recent Searches
                  </h3>
                  <div className="space-y-1">
                    {recentSearches.map((query, index) => (
                      <motion.button
                        key={query}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleSearch(query)}
                        className={cn(
                          "w-full flex items-center gap-3 p-3 rounded-lg",
                          "hover:bg-muted transition-colors",
                          "group"
                        )}
                      >
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span className="flex-1 text-left">{query}</span>
                        <button
                          onClick={(e) => clearRecentSearch(query, e)}
                          className="p-1 rounded-full opacity-0 group-hover:opacity-100 hover:bg-muted-foreground/20 transition-all"
                          aria-label={`Remove ${query} from recent searches`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {/* Suggestions */}
              <div className="px-4 py-4">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  {searchQuery ? "Suggestions" : "Popular"}
                </h3>
                <div className="space-y-1">
                  {filteredSuggestions.map((suggestion, index) => {
                    const Icon = suggestion.icon;
                    return (
                      <motion.button
                        key={suggestion.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 + index * 0.05 }}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className={cn(
                          "w-full flex items-center gap-3 p-3 rounded-lg",
                          "hover:bg-muted transition-colors active:scale-[0.98]"
                        )}
                        data-testid={`search-suggestion-${suggestion.id}`}
                      >
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-medium">{suggestion.label}</div>
                          <div className="text-xs text-muted-foreground capitalize">
                            {suggestion.type}
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* No results message */}
              {searchQuery && filteredSuggestions.length === 0 && (
                <div className="px-4 py-8 text-center">
                  <Search className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    No results found for "{searchQuery}"
                  </p>
                  <button
                    onClick={() => handleSearch(searchQuery)}
                    className="mt-3 text-primary font-medium"
                  >
                    Search anyway
                  </button>
                </div>
              )}
            </div>

            {/* Safe area padding */}
            <div className="h-safe-area-inset-bottom" />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default MobileSearchOverlay;
